import { th } from './th';

import type { Dictionary } from './th';

/**
 * Thai and English only in v1. The reference app also ships Japanese, but our market is
 * Thailand and an unmaintained third locale is worse than an absent one — `Dictionary`
 * makes adding it later a mechanical job.
 */
export const LOCALES = ['th', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'th';

/**
 * `en` is typed as `Dictionary`, so the compiler rejects a locale that has drifted from
 * `th`. It loads lazily — a Thai-only session never downloads it.
 */
const loaders: Record<Locale, () => Promise<Dictionary>> = {
  th: async () => th,
  en: async () => (await import('./en')).en,
};

export async function loadDictionary(locale: Locale): Promise<Dictionary> {
  const load = loaders[locale] ?? loaders[DEFAULT_LOCALE];
  return load();
}

/** Dot path into the dictionary, e.g. `project.tabs.overview`. */
export type MessageKey = DotPaths<Dictionary>;

type DotPaths<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string ? K : `${K}.${DotPaths<T[K]>}`;
    }[keyof T & string];

/**
 * Resolves a dot path and substitutes `{name}` placeholders.
 *
 * A missing key returns the key itself rather than throwing: a broken label should not take
 * a whole screen down, and the key is visible enough in the UI to get noticed and fixed.
 */
export function translate(
  dict: Dictionary,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const raw = key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, dict);

  if (typeof raw !== 'string') return key;
  if (!vars) return raw;

  return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export { th };
export type { Dictionary };
